import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  Mail, 
  Menu, 
  X, 
  ChevronDown, 
  MapPin, 
  Clock,
  MessageCircle,
  Sparkles
} from 'lucide-react';
import { FaFacebookF, FaInstagram, FaTwitter } from 'react-icons/fa';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [navbarHeight, setNavbarHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      const nav = document.querySelector('nav');
      if (nav) {
        setNavbarHeight(nav.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
    setOpenDropdown(null);
  };

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Diamond Jewellery', href: '/diamond-jewellery' },
    { name: 'Rings', href: '/rings' },
    { name: 'Necklaces', href: '/necklaces'},
    { name: 'Shop', href: '/shop' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 w-full">
        {/* Top Bar - Fully Responsive with safe padding */}
        <div className="bg-gradient-to-r from-amber-700 via-amber-600 to-amber-500 text-white shadow-md relative">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 pointer-events-none"></div>
          <div className="w-full px-3 sm:px-4 py-2 sm:py-2.5 md:py-3">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-[11px] sm:text-xs md:text-sm">
              
              {/* Contact Info - Better mobile layout without overflow */}
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-6">
                <a 
                  href="mailto:info@skkjewellers.com" 
                  className="flex items-center gap-1.5 hover:text-amber-200 transition-all duration-300 hover:scale-105 group"
                >
                  <Mail size={12} className="sm:size-[14px] group-hover:rotate-12 transition-transform" />
                  <span className="hidden xs:inline">info@skkjewellers.com</span>
                  <span className="xs:hidden">Email</span>
                </a>
                <a 
                  href="tel:+919839949895" 
                  className="flex items-center gap-1.5 hover:text-amber-200 transition-all duration-300 hover:scale-105 group"
                >
                  <Phone size={12} className="sm:size-[14px] group-hover:rotate-12 transition-transform" />
                  <span className="whitespace-nowrap">+91 98399 49895</span>
                </a>
                <a 
                  href="tel:+919285668748" 
                  className="hidden sm:flex items-center gap-1.5 hover:text-amber-200 transition-all duration-300 hover:scale-105 group"
                >
                  <Phone size={12} className="sm:size-[14px] group-hover:rotate-12 transition-transform" />
                  <span>+91 92856 68748</span>
                </a>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex items-center gap-3 sm:gap-4">
                <a 
                  href="#" 
                  className="hover:text-amber-200 transition-all duration-300 hover:scale-110 hover:rotate-12"
                  aria-label="Facebook"
                >
                  <FaFacebookF size={11} className="sm:size-[14px]" />
                </a>
                <a 
                  href="#" 
                  className="hover:text-amber-200 transition-all duration-300 hover:scale-110 hover:rotate-12"
                  aria-label="Instagram"
                >
                  <FaInstagram size={11} className="sm:size-[14px]" />
                </a>
                <a 
                  href="#" 
                  className="hidden sm:block hover:text-amber-200 transition-all duration-300 hover:scale-110 hover:rotate-12"
                  aria-label="Twitter"
                >
                  <FaTwitter size={11} className="sm:size-[14px]" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] border-b border-amber-100 relative">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
          
          <div className="w-full px-3 sm:px-4 md:px-8">
            <div className="flex justify-between items-center min-h-[60px] sm:min-h-[64px] md:min-h-[70px] lg:min-h-[80px]">
              
              {/* Logo */}
              <div className="flex items-center transition-all duration-500 hover:scale-105 cursor-pointer relative group py-1">
                <div className="absolute inset-0 bg-amber-400/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <img 
                  src="/logo.png" 
                  alt="SKK Jewellers Logo" 
                  className="h-10 sm:h-12 md:h-14 lg:h-20 w-auto object-contain relative z-10"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/120x60?text=SKK+Jewellers';
                  }}
                />
              </div>

              {/* Desktop Navigation - Hidden on mobile */}
              <div className="hidden lg:flex items-center space-x-5 xl:space-x-9">
                {navLinks.map((link, index) => (
                  <div key={link.name} className="relative group">
                    <a
                      href={link.href}
                      className="text-gray-700 hover:text-amber-600 font-medium text-sm xl:text-base py-6 transition-all duration-300 relative block whitespace-nowrap"
                      style={{
                        animation: `fadeInDown 0.5s ease-out ${index * 0.05}s both`
                      }}
                    >
                      {link.name}
                      <span className="absolute bottom-3 left-1/2 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500 group-hover:w-full transition-all duration-300 group-hover:left-0 rounded-full"></span>
                      <span className="absolute -top-1 left-1/2 w-1 h-1 bg-amber-400 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-1/2"></span>
                    </a>
                  </div>
                ))}
              </div>

              {/* CTA Button - Desktop */}
              <div className="hidden lg:block">
                <button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 xl:px-5 py-1.5 xl:py-2 rounded-full text-sm font-medium shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 group">
                  <Sparkles size={14} className="xl:size-[16px] group-hover:rotate-12 transition-transform" />
                  <span>Get Quote</span>
                </button>
              </div>

              {/* ===== FIXED: Mobile Menu Button - Centered properly ===== */}
              {/* The three dots button will now be positioned correctly on the right side without breaking layout */}
              <button 
                onClick={toggleMenu}
                className="lg:hidden flex items-center justify-center text-gray-700 hover:text-amber-600 focus:outline-none p-2.5 rounded-xl bg-gray-50 transition-all duration-300 hover:scale-110 active:scale-95 z-50 shadow-sm"
                aria-label="Menu"
              >
                {isMenuOpen ? <X size={22} className="sm:size-[24px]" /> : <Menu size={22} className="sm:size-[24px]" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Dynamic Spacer - Prevents content hiding */}
      <div style={{ height: `${navbarHeight}px` }} className="w-full"></div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-all duration-500 lg:hidden ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={closeMenu}
      ></div>

      {/* ===== FIXED: Mobile Menu Panel - Properly centered content, no right-side breakage ===== */}
      <div 
        className={`fixed top-0 right-0 h-full w-[85%] max-w-[360px] bg-white shadow-2xl z-50 transition-transform duration-500 ease-in-out lg:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile Menu Header */}
        <div className="bg-gradient-to-r from-amber-700 to-orange-600 p-5 sm:p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-white/5 transform -skew-x-12 translate-x-1/2"></div>
          <div className="flex justify-between items-center relative z-10">
            <div>
              <h2 className="text-white font-bold text-xl sm:text-2xl tracking-wide">SKK Jewellers</h2>
              <p className="text-amber-100 text-xs sm:text-sm mt-1">Since 1995 | Trust Since Generations</p>
            </div>
            <button 
              onClick={toggleMenu}
              className="text-white p-2 hover:bg-white/20 rounded-full transition-all duration-300 hover:rotate-90"
            >
              <X size={22} className="sm:size-[24px]" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Links - Scrollable area */}
        <div className="p-4 sm:p-5 space-y-1 overflow-y-auto h-[calc(100%-130px)] no-scrollbar">
          {navLinks.map((link, index) => (
            <div key={link.name}>
              <a
                href={link.href}
                onClick={closeMenu}
                className="block text-gray-700 hover:text-amber-600 py-3.5 sm:py-4 px-3 rounded-xl hover:bg-amber-50 transition-all duration-300 hover:translate-x-2 font-medium border-b border-gray-100 text-base sm:text-lg"
                style={{
                  animation: isMenuOpen ? `slideInLeft 0.4s ease-out ${index * 0.03}s both` : 'none'
                }}
              >
                {link.name}
              </a>
            </div>
          ))}
          
          {/* Mobile Contact Info */}
          <div className="pt-6 sm:pt-8 mt-4 border-t border-gray-200">
            <p className="text-amber-600 font-semibold text-sm sm:text-base mb-3 sm:mb-4 px-2 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-amber-400 rounded-full"></span>
              Contact Us
              <span className="w-5 h-0.5 bg-amber-400 rounded-full"></span>
            </p>
            <a 
              href="tel:+919839949895" 
              className="flex items-center gap-3 py-3 sm:py-3.5 px-3 rounded-xl hover:bg-amber-50 transition-all duration-300 group text-sm sm:text-base"
            >
              <Phone size={16} className="sm:size-[18px] text-amber-600 group-hover:scale-110 transition-transform" />
              <span className="text-gray-700 font-medium">+91 98399 49895</span>
            </a>
            <a 
              href="tel:+919285668748" 
              className="flex items-center gap-3 py-3 sm:py-3.5 px-3 rounded-xl hover:bg-amber-50 transition-all duration-300 group text-sm sm:text-base"
            >
              <Phone size={16} className="sm:size-[18px] text-amber-600 group-hover:scale-110 transition-transform" />
              <span className="text-gray-700 font-medium">+91 92856 68748</span>
            </a>
            <a 
              href="https://wa.me/919839949895" 
              className="flex items-center gap-3 py-3 sm:py-3.5 px-3 rounded-xl hover:bg-amber-50 transition-all duration-300 group text-sm sm:text-base"
            >
              <MessageCircle size={16} className="sm:size-[18px] text-green-600 group-hover:scale-110 transition-transform" />
              <span className="text-gray-700 font-medium">WhatsApp: +91 98399 49895</span>
            </a>
            <a 
              href="mailto:info@skkjewellers.com" 
              className="flex items-center gap-3 py-3 sm:py-3.5 px-3 rounded-xl hover:bg-amber-50 transition-all duration-300 group text-sm sm:text-base"
            >
              <Mail size={16} className="sm:size-[18px] text-amber-600 group-hover:scale-110 transition-transform" />
              <span className="text-gray-700">info@skkjewellers.com</span>
            </a>
            <div className="flex items-start gap-3 py-3 sm:py-3.5 px-3">
              <MapPin size={16} className="sm:size-[18px] text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700 text-sm sm:text-base leading-relaxed">Arya Samaj Road, Near PNB ATM, Ballia - 277001</span>
            </div>
            <div className="flex items-center gap-3 py-3 sm:py-3.5 px-3">
              <Clock size={16} className="sm:size-[18px] text-amber-600 flex-shrink-0" />
              <span className="text-gray-700 text-sm sm:text-base">Mon-Sun: 10:00 AM - 8:00 PM</span>
            </div>
          </div>

          {/* Mobile Social Icons */}
          <div className="pt-6 flex gap-5 justify-center">
            <a href="#" className="text-gray-500 hover:text-amber-600 p-2.5 hover:bg-amber-50 rounded-full transition-all duration-300 hover:scale-110">
              <FaFacebookF size={20} className="sm:size-[22px]" />
            </a>
            <a href="#" className="text-gray-500 hover:text-amber-600 p-2.5 hover:bg-amber-50 rounded-full transition-all duration-300 hover:scale-110">
              <FaInstagram size={20} className="sm:size-[22px]" />
            </a>
            <a href="#" className="text-gray-500 hover:text-amber-600 p-2.5 hover:bg-amber-50 rounded-full transition-all duration-300 hover:scale-110">
              <FaTwitter size={20} className="sm:size-[22px]" />
            </a>
          </div>

          {/* Store Address Badge */}
          <div className="mt-6 sm:mt-8 mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-sm">
            <p className="text-sm sm:text-base text-amber-800 text-center font-medium leading-relaxed">
              ✨ Visit our showroom at Arya Samaj Road, Ballia<br />
              🎁 Exclusive offers on wedding collection
            </p>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-25px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        /* Extra small devices (below 480px) - show email text on larger phones */
        @media (min-width: 480px) {
          .xs\\:inline {
            display: inline;
          }
        }
        
        /* Hide email text on very small devices to prevent wrapping */
        @media (max-width: 479px) {
          .xs\\:inline {
            display: none;
          }
        }
        
        /* Prevent horizontal scroll on mobile */
        @media (max-width: 640px) {
          nav {
            position: fixed;
            width: 100%;
            top: 0;
            left: 0;
            right: 0;
          }
          
          /* Ensure no overflow on body */
          body {
            overflow-x: hidden;
            width: 100%;
          }
          
          /* Make top bar text smaller on very narrow screens */
          .top-bar-text {
            font-size: 10px;
          }
        }
        
        /* Custom scrollbar hide for mobile menu */
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default Navbar;